import { useState, useCallback } from 'react';
import { VideoFormState, SceneState } from '../types/heygen';
import type { HeyGenPayload, HeyGenDimension, TalkingStyle } from '../../../types/avatar-video';
import { PROMPT_PRESETS } from '../constants/prompt-presets';
import { buildPayload } from '../utils/build-payload';

const INITIAL_SCENE: SceneState = {
  vestuario: '',
  ambiente: '',
  plano: '',
  script: '',
  onlyVoice: false
};

const INITIAL_STATE: VideoFormState = {
  title: '',
  voiceId: '720470f8a9f24d93b77611414a2e25b7', // por el momento siempre vamos a usar este
  talkingStyle: 'expressive', // idem
  promptKey: 'professional', // idem
  scenes: [INITIAL_SCENE]
};

export const useVideoForm = () => {
  const [form, setForm] = useState<VideoFormState>(INITIAL_STATE);

  const setField = useCallback(<K extends keyof VideoFormState>(key: K, value: VideoFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  const setTalkingStyle = useCallback((style: TalkingStyle) => {
    setForm(prev => ({ ...prev, talkingStyle: style }));
  }, []);

  const resetScene = (index: number) => {
    setForm(prev => ({
      ...prev,
      scenes: prev.scenes.map((scene, i) => (i === index ? { ...INITIAL_SCENE } : scene))
    }));
  };

  const resetForm = useCallback(() => {
    setForm(INITIAL_STATE);
  }, []);

  const loadForm = useCallback((state: VideoFormState) => {
    setForm(state);
  }, []);

  const getPayload = useCallback(
    (dimension?: HeyGenDimension, selectedResolution?: string): HeyGenPayload => {
      return buildPayload(form, PROMPT_PRESETS, dimension, selectedResolution);
    },
    [form]
  );

  const addScene = () =>
    setForm(prev => {
      const last = prev.scenes[prev.scenes.length - 1];
      const newScene: SceneState =
        last && !last.onlyVoice
          ? { vestuario: last.vestuario, ambiente: last.ambiente, plano: last.plano, script: '', onlyVoice: false }
          : { ...INITIAL_SCENE };
      return { ...prev, scenes: [...prev.scenes, newScene] };
    });

  const removeScene = (index: number) =>
    setForm(prev => ({ ...prev, scenes: prev.scenes.filter((_, i) => i !== index) }));

  const duplicateScene = (index: number) =>
    setForm(prev => {
      const { sceneId: _sceneId, ...rest } = prev.scenes[index];
      const copy = { ...rest };
      const next = [...prev.scenes];
      next.splice(index + 1, 0, copy);
      return { ...prev, scenes: next };
    });

  const updateScene = (index: number, key: keyof SceneState, value: string) => {
    setForm(prev => ({
      ...prev,
      scenes: prev.scenes.map((scene, i) => (i === index ? { ...scene, [key]: value } : scene))
    }));
  };

  const setSceneOnlyVoice = (index: number, onlyVoice: boolean) => {
    setForm(prev => ({
      ...prev,
      scenes: prev.scenes.map((scene, i) => (i === index ? { ...scene, onlyVoice } : scene))
    }));
  };

  const isValid =
    form.title.trim() !== '' &&
    form.scenes.length > 0 &&
    form.scenes.every(s => {
      if (s.script.trim() === '') return false;
      // Las escenas solo voz no requieren vestuario/ambiente/plano (no se genera video).
      if (s.onlyVoice) return true;
      return s.vestuario !== '' && s.ambiente !== '' && s.plano !== '';
    });

  return {
    form,
    setField,
    setTalkingStyle,
    resetScene,
    resetForm,
    loadForm,
    getPayload,
    addScene,
    removeScene,
    duplicateScene,
    updateScene,
    setSceneOnlyVoice,
    isValid
  };
};
